import React, {CSSProperties, type Ref} from "react";
import {connect} from "react-redux";
import {css, cx} from "@panda/css";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {Expandable} from "@pg-design/expandable-module";
import {ChevronDownIcon, ChevronUpIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {form} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {Country} from "../../../region/types/Country";
import {resetFetchAllSearchLists, stopFetchAllSearchLists} from "../../actions/fetch_search_all_action";
import {optimizedFetchPlaceList} from "../../actions/fetch_search_places_action";
import {clearLabelOfActiveValue, resetActiveDropdownItem, setNextDropdownItem, setPreviousDropdownItem} from "../../actions/set_active_dropdown_item";
import {setItemOnEnter} from "../../actions/set_item_on_enter";
import {setTravelTimeData} from "../../actions/set_travel_time";
import {updateCurrentTab} from "../../actions/update_current_tab";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchAutocompleteInputWithSlot} from "./SearchAutocompleteInputWithSlot";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteFormValues,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps
} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteController} from "./useSearchAutocompleteController";

export type {ISearchAutocompleteFormValues, SearchAutocompleteController} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const SearchAutocompleteC: React.FC<IProps> = (props) => {
    const controller = useSearchAutocompleteController(props);
    const selectedSearch = props.values?.search;

    return (
        <RelativeWrapper
            isInModal={props.isInModal}
            disablePadding={controller.disableRelativeWrapperPadding}
            onKeyDown={controller.onKeyDownHandler}
            ref={controller.wrapperRef}
        >
            {props.renderLayout ? (
                props.renderLayout(controller)
            ) : (
                <>
                    {props.isHomepage && (
                        <div className={tabsWrapperStyle} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.COUNTRY_TAB}>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.country === Country.POLAND ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.POLAND)}
                            >
                                W Polsce
                            </button>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.country === Country.SPAIN ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.SPAIN)}
                            >
                                W Hiszpanii
                            </button>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.currentTab === SearchTab.Holiday ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Holiday)}
                            >
                                Wakacyjne
                            </button>
                        </div>
                    )}
                    <SearchbarContainer disableExtraMargins={controller.disableSearchbarExtraMargins} className={controller.searchBarContainerClassName}>
                        <SearchAutocompleteInputWithSlot {...controller.inputProps} slot={props.slot} slotOrder={props.slotOrder} />
                        {controller.shouldRenderDistanceFilter && (
                            <InputWrapper className={distanceWrapperRightStyle} noPadding>
                                <SearchDistanceFilter {...controller.distanceFilterProps} />
                            </InputWrapper>
                        )}
                    </SearchbarContainer>
                    <SearchDropdown {...controller.dropdownProps}>
                        <div className={searchDropdownContentWrapperStyle}>{controller.renderDropdownList()}</div>
                    </SearchDropdown>
                    {controller.showSelectedUnderInput && selectedSearch && "regions" in selectedSearch && (
                        <div className={regionsHolderStyle}>
                            {selectedSearch.regions.length > 2 ? (
                                <Expandable
                                    isExpandedByDefault={false}
                                    collapsedHeight={controller.isMobile ? 0 : 110}
                                    customCollapseButton={({isExpanded, toggleCollapse}) => (
                                        <Button
                                            variant="none_secondary"
                                            size="x-small"
                                            type="button"
                                            iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                            onClick={toggleCollapse}
                                            className={regionExpandableButtonStyle}
                                        >
                                            {isExpanded
                                                ? "Zwiń wybrane lokalizacje"
                                                : `Rozwiń wybrane lokalizacje ${selectedSearch.tabType === SearchTab.Regions ? `(${selectedSearch.regions.length})` : ""}`}
                                        </Button>
                                    )}
                                    customShadowStyle={customExpandableShadowStyle}
                                >
                                    <div className={regionsExpandableStyle}>
                                        {selectedSearch.regions.map((region) => (
                                            <Pill
                                                key={region.id}
                                                size="large"
                                                className={regionPillStyle}
                                                onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                            >
                                                {!props.includeDistanceFilter && (
                                                    <MapMarkerOutlineIcon fill="var(--colors-secondary)" size="2.4" className={regionMarkerIconStyle} />
                                                )}
                                                <div className={titleHolderStyle}>
                                                    {region.full_name_reverted}
                                                    {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                                </div>
                                            </Pill>
                                        ))}
                                    </div>
                                </Expandable>
                            ) : (
                                selectedSearch.regions.map((region) => (
                                    <Pill
                                        key={region.id}
                                        size="large"
                                        className={regionPillStyle}
                                        onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                    >
                                        {!props.includeDistanceFilter && (
                                            <MapMarkerOutlineIcon fill="var(--colors-secondary)" size="2.4" className={regionMarkerIconStyle} />
                                        )}
                                        <div className={titleHolderStyle}>
                                            {region.full_name_reverted}
                                            {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                        </div>
                                    </Pill>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}
        </RelativeWrapper>
    );
};

export const SearchAutocomplete = connect(mapStateToProps, mapActionsToProps)(form<ISearchAutocompleteFormValues, IProps>(SearchAutocompleteC));

function mapStateToProps(state: IRPStore): ISearchAutocompleteStateProps {
    return {
        search: state.search
    };
}

function mapActionsToProps(dispatch: Dispatch): ISearchAutocompleteActionsProps {
    return {
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                setNextDropdownItem,
                setPreviousDropdownItem,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                updateCurrentTab,
                optimizedFetchPlaceList,
                setItemOnEnter,
                clearLabelOfActiveValue,
                setTravelTimeData
            },
            dispatch
        )
    };
}

interface IRelativeWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
    isInModal?: boolean;
    disablePadding?: boolean;
    ref?: Ref<HTMLDivElement>;
}

export const RelativeWrapper = (props: IRelativeWrapperProps) => {
    const {isInModal, disablePadding, className, ref, ...restProps} = props;

    return (
        <div
            {...restProps}
            ref={ref}
            className={cx(
                relativeWrapperBaseStyle,
                !isInModal && relativeWrapperOutsideModalStyle,
                disablePadding ? relativeWrapperNoPaddingStyle : undefined,
                className
            )}
        />
    );
};

interface ISearchDropdownProps {
    isOpen: boolean;
    isInModal?: boolean;
    isPortaled?: boolean;
    isInputWithDistanceFilter?: boolean;
    isHomepage?: boolean;
    activeRegionSearchTab?: RegionSearchTabsType | null;
    minWidth?: string;
    top?: string;
    className?: string;
    style?: CSSProperties;
    children?: React.ReactNode;
    ref?: Ref<HTMLDivElement>;
    "data-testid"?: string;
}

export const SearchDropdown = (props: ISearchDropdownProps) => {
    const {
        isOpen,
        isInModal,
        isPortaled,
        isInputWithDistanceFilter,
        isHomepage,
        activeRegionSearchTab,
        minWidth,
        top,
        className,
        style,
        children,
        ref,
        "data-testid": dataTestId
    } = props;

    const isVisible = isOpen || isInModal;
    const isAnchored = !isInModal && !isPortaled;
    const resolvedTop = top ?? (isHomepage ? "11rem" : "6rem");
    const resolvedMinWidth = !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? undefined : minWidth || "600px";
    const districtsMinWidth = !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? "836px" : minWidth || "600px";

    return (
        <div
            ref={ref}
            data-testid={dataTestId}
            className={cx(
                searchDropdownBaseStyle,
                isVisible ? searchDropdownVisibleStyle : searchDropdownHiddenStyle,
                isAnchored && searchDropdownAnchoredStyle,
                isAnchored && (isInputWithDistanceFilter ? searchDropdownWithDistanceStyle : searchDropdownWithoutDistanceStyle),
                isPortaled && searchDropdownPortaledStyle,
                !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS && searchDropdownDistrictsMinWidthStyle,
                className
            )}
            style={
                {
                    ...(isAnchored ? {"--search-dropdown-top": resolvedTop} : undefined),
                    ...(resolvedMinWidth ? {"--search-dropdown-min-width": resolvedMinWidth} : undefined),
                    ...(districtsMinWidth ? {"--search-dropdown-districts-min-width": districtsMinWidth} : undefined),
                    ...style
                } as CSSProperties
            }
        >
            {children}
        </div>
    );
};

interface ISearchbarContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    disableExtraMargins?: boolean;
}

export const SearchbarContainer = (props: ISearchbarContainerProps) => {
    const {disableExtraMargins, className, ...restProps} = props;

    return (
        <div
            {...restProps}
            className={cx(
                searchbarContainerBaseStyle,
                disableExtraMargins ? searchbarContainerTightMarginsStyle : searchbarContainerDefaultMarginsStyle,
                className
            )}
        />
    );
};

const relativeWrapperBaseStyle = css({
    position: "relative",
    display: "flex",
    flexGrow: 1,
    flexShrink: 1,
    flexDirection: "column",
    padding: 2,

    md: {
        padding: 0,
        maxHeight: "100%",
        width: "100%"
    }
});

const relativeWrapperOutsideModalStyle = css({
    height: "calc(100% - 6rem)"
});

const relativeWrapperNoPaddingStyle = css({
    padding: 0
});

const searchDropdownBaseStyle = css({
    width: "100%",
    flexDirection: "column",
    animationStyle: "fade-in",

    md: {
        boxShadow: "4",
        minWidth: "var(--search-dropdown-min-width, 600px)"
    },

    lg: {
        minWidth: "var(--search-dropdown-districts-min-width, var(--search-dropdown-min-width, 600px))"
    }
});

const searchDropdownVisibleStyle = css({
    display: "flex"
});

const searchDropdownHiddenStyle = css({
    display: "none"
});

const searchDropdownAnchoredStyle = css({
    position: "absolute",
    py: 1,
    px: 1,
    margin: 0,
    zIndex: 1001,
    top: "var(--search-dropdown-top)",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)"
});

const searchDropdownWithDistanceStyle = css({
    left: "120px",
    width: "calc(100% - 120px)",

    "@media (max-width: 480px)": {
        left: 0,
        width: "100%"
    }
});

const searchDropdownWithoutDistanceStyle = css({
    left: 0,
    width: "100%"
});

const searchDropdownPortaledStyle = css({
    position: "fixed",
    margin: 0,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)"
});

const searchDropdownDistrictsMinWidthStyle = css({
    lg: {
        minWidth: "836px"
    }
});

const searchDropdownContentWrapperStyle = css({
    py: 2,
    px: 1
});

const distanceWrapperRightStyle = css({
    maxWidth: "120px",
    width: "30%",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 0,
    height: "50px"
});

const searchbarContainerBaseStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    maxHeight: "4.8rem",

    "& > div:nth-of-type(2)": {
        marginBottom: 0,
        width: "100%",
        zIndex: "initial"
    }
});

const searchbarContainerTightMarginsStyle = css({
    mb: 0.5,

    "@media (max-width: 1024px)": {
        mb: 0.5
    }
});

const searchbarContainerDefaultMarginsStyle = css({
    "@media (max-width: 1024px)": {
        mb: 3
    }
});

const tabsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: "var(--colors-gray-200)",
    overflow: "hidden",
    maxHeight: "4.8rem"
});

const tabStyle = css({
    width: "100%",
    border: 0,
    borderRadius: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "4.8rem",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-background-200)"
    }
});

const tabActiveStyle = css({
    backgroundColor: "var(--colors-background-200)"
});

const tabInactiveStyle = css({
    backgroundColor: "var(--colors-background-100)"
});

const regionPillStyle = css({
    py: 0,
    px: 1,
    height: "4.8rem",
    display: "flex",
    alignItems: "center",
    flexDirection: "row"
});

const regionsHolderStyle = css({
    my: 2,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 1,

    "& > div:last-of-type": {
        marginRight: 0
    }
});

const titleHolderStyle = css({
    pr: 1,
    flexGrow: 1,
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const regionsExpandableStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const regionExpandableButtonStyle = css({
    marginTop: "-2rem",
    width: "100%"
});

const regionMarkerIconStyle = css({
    pr: 1
});

const customExpandableShadowStyle = {
    background: "linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%)"
};
import React, {CSSProperties, type Ref} from "react";
import {connect} from "react-redux";
import {css, cx} from "@panda/css";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {Expandable} from "@pg-design/expandable-module";
import {ChevronDownIcon, ChevronUpIcon, MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {form} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {Country} from "../../../region/types/Country";
import {resetFetchAllSearchLists, stopFetchAllSearchLists} from "../../actions/fetch_search_all_action";
import {optimizedFetchPlaceList} from "../../actions/fetch_search_places_action";
import {clearLabelOfActiveValue, resetActiveDropdownItem, setNextDropdownItem, setPreviousDropdownItem} from "../../actions/set_active_dropdown_item";
import {setItemOnEnter} from "../../actions/set_item_on_enter";
import {setTravelTimeData} from "../../actions/set_travel_time";
import {updateCurrentTab} from "../../actions/update_current_tab";
import {RegionSearchTabsType} from "../../types/RegionSearchTabsType";
import {SearchTab} from "../../types/SearchTab";
import {SearchDistanceFilter} from "../SearchDistanceFilter";
import {SearchAutocompleteInputWithSlot} from "./SearchAutocompleteInputWithSlot";
import {
    ISearchAutocompleteActionsProps,
    ISearchAutocompleteFormValues,
    ISearchAutocompleteOwnProps,
    ISearchAutocompleteStateProps
} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteController} from "./useSearchAutocompleteController";

export type {ISearchAutocompleteFormValues, SearchAutocompleteController} from "./SearchAutocompleteTypes";

interface IProps extends ISearchAutocompleteOwnProps, ISearchAutocompleteStateProps, ISearchAutocompleteActionsProps {}

const SearchAutocompleteC: React.FC<IProps> = (props) => {
    const controller = useSearchAutocompleteController(props);
    const selectedSearch = props.values?.search;

    return (
        <RelativeWrapper
            isInModal={props.isInModal}
            disablePadding={controller.disableRelativeWrapperPadding}
            onKeyDown={controller.onKeyDownHandler}
            ref={controller.wrapperRef}
        >
            {props.renderLayout ? (
                props.renderLayout(controller)
            ) : (
                <>
                    {props.isHomepage && (
                        <div className={tabsWrapperStyle} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.COUNTRY_TAB}>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.country === Country.POLAND ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.POLAND)}
                            >
                                W Polsce
                            </button>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.country === Country.SPAIN ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Regions, Country.SPAIN)}
                            >
                                W Hiszpanii
                            </button>
                            <button
                                type="button"
                                className={cx(tabStyle, controller.search.currentTabData.currentTab === SearchTab.Holiday ? tabActiveStyle : tabInactiveStyle)}
                                onClick={() => controller.onTabTypeClick(SearchTab.Holiday)}
                            >
                                Wakacyjne
                            </button>
                        </div>
                    )}
                    <SearchbarContainer disableExtraMargins={controller.disableSearchbarExtraMargins} className={controller.searchBarContainerClassName}>
                        <SearchAutocompleteInputWithSlot {...controller.inputProps} slot={props.slot} slotOrder={props.slotOrder} />
                        {controller.shouldRenderDistanceFilter && (
                            <InputWrapper className={distanceWrapperRightStyle} noPadding>
                                <SearchDistanceFilter {...controller.distanceFilterProps} />
                            </InputWrapper>
                        )}
                    </SearchbarContainer>
                    <SearchDropdown {...controller.dropdownProps}>
                        <div className={searchDropdownContentWrapperStyle}>{controller.renderDropdownList()}</div>
                    </SearchDropdown>
                    {controller.showSelectedUnderInput && selectedSearch && "regions" in selectedSearch && (
                        <div className={regionsHolderStyle}>
                            {selectedSearch.regions.length > 2 ? (
                                <Expandable
                                    isExpandedByDefault={false}
                                    collapsedHeight={controller.isMobile ? 0 : 110}
                                    customCollapseButton={({isExpanded, toggleCollapse}) => (
                                        <Button
                                            variant="none_secondary"
                                            size="x-small"
                                            type="button"
                                            iconRight={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                                            onClick={toggleCollapse}
                                            className={regionExpandableButtonStyle}
                                        >
                                            {isExpanded
                                                ? "Zwiń wybrane lokalizacje"
                                                : `Rozwiń wybrane lokalizacje ${selectedSearch.tabType === SearchTab.Regions ? `(${selectedSearch.regions.length})` : ""}`}
                                        </Button>
                                    )}
                                    customShadowStyle={customExpandableShadowStyle}
                                >
                                    <div className={regionsExpandableStyle}>
                                        {selectedSearch.regions.map((region) => (
                                            <Pill
                                                key={region.id}
                                                size="large"
                                                className={regionPillStyle}
                                                onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                            >
                                                {!props.includeDistanceFilter && (
                                                    <MapMarkerOutlineIcon fill="var(--colors-secondary)" size="2.4" className={regionMarkerIconStyle} />
                                                )}
                                                <div className={titleHolderStyle}>
                                                    {region.full_name_reverted}
                                                    {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                                </div>
                                            </Pill>
                                        ))}
                                    </div>
                                </Expandable>
                            ) : (
                                selectedSearch.regions.map((region) => (
                                    <Pill
                                        key={region.id}
                                        size="large"
                                        className={regionPillStyle}
                                        onCloseClick={() => controller.onRemoveRegionTagClick(region.id)}
                                    >
                                        {!props.includeDistanceFilter && (
                                            <MapMarkerOutlineIcon fill="var(--colors-secondary)" size="2.4" className={regionMarkerIconStyle} />
                                        )}
                                        <div className={titleHolderStyle}>
                                            {region.full_name_reverted}
                                            {props.includeDistanceFilter && !!controller.pillDistanceValue && ` (+${controller.pillDistanceValue}km)`}
                                        </div>
                                    </Pill>
                                ))
                            )}
                        </div>
                    )}
                </>
            )}
        </RelativeWrapper>
    );
};

export const SearchAutocomplete = connect(mapStateToProps, mapActionsToProps)(form<ISearchAutocompleteFormValues, IProps>(SearchAutocompleteC));

function mapStateToProps(state: IRPStore): ISearchAutocompleteStateProps {
    return {
        search: state.search
    };
}

function mapActionsToProps(dispatch: Dispatch): ISearchAutocompleteActionsProps {
    return {
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                setNextDropdownItem,
                setPreviousDropdownItem,
                stopFetchAllSearchLists,
                resetFetchAllSearchLists,
                updateCurrentTab,
                optimizedFetchPlaceList,
                setItemOnEnter,
                clearLabelOfActiveValue,
                setTravelTimeData
            },
            dispatch
        )
    };
}

interface IRelativeWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
    isInModal?: boolean;
    disablePadding?: boolean;
    ref?: Ref<HTMLDivElement>;
}

export const RelativeWrapper = (props: IRelativeWrapperProps) => {
    const {isInModal, disablePadding, className, ref, ...restProps} = props;

    return (
        <div
            {...restProps}
            ref={ref}
            className={cx(
                relativeWrapperBaseStyle,
                !isInModal && relativeWrapperOutsideModalStyle,
                disablePadding ? relativeWrapperNoPaddingStyle : undefined,
                className
            )}
        />
    );
};

interface ISearchDropdownProps {
    isOpen: boolean;
    isInModal?: boolean;
    isPortaled?: boolean;
    isInputWithDistanceFilter?: boolean;
    isHomepage?: boolean;
    activeRegionSearchTab?: RegionSearchTabsType | null;
    minWidth?: string;
    top?: string;
    className?: string;
    style?: CSSProperties;
    children?: React.ReactNode;
    ref?: Ref<HTMLDivElement>;
    "data-testid"?: string;
}

export const SearchDropdown = (props: ISearchDropdownProps) => {
    const {
        isOpen,
        isInModal,
        isPortaled,
        isInputWithDistanceFilter,
        isHomepage,
        activeRegionSearchTab,
        minWidth,
        top,
        className,
        style,
        children,
        ref,
        "data-testid": dataTestId
    } = props;

    const isVisible = isOpen || isInModal;
    const isAnchored = !isInModal && !isPortaled;
    const resolvedTop = top ?? (isHomepage ? "11rem" : "6rem");
    const resolvedMinWidth = !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? undefined : minWidth || "600px";
    const districtsMinWidth = !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS ? "836px" : minWidth || "600px";

    return (
        <div
            ref={ref}
            data-testid={dataTestId}
            className={cx(
                searchDropdownBaseStyle,
                isVisible ? searchDropdownVisibleStyle : searchDropdownHiddenStyle,
                isAnchored && searchDropdownAnchoredStyle,
                isAnchored && (isInputWithDistanceFilter ? searchDropdownWithDistanceStyle : searchDropdownWithoutDistanceStyle),
                isPortaled && searchDropdownPortaledStyle,
                !minWidth && activeRegionSearchTab === RegionSearchTabsType.DISTRICTS && searchDropdownDistrictsMinWidthStyle,
                className
            )}
            style={
                {
                    ...(isAnchored ? {"--search-dropdown-top": resolvedTop} : undefined),
                    ...(resolvedMinWidth ? {"--search-dropdown-min-width": resolvedMinWidth} : undefined),
                    ...(districtsMinWidth ? {"--search-dropdown-districts-min-width": districtsMinWidth} : undefined),
                    ...style
                } as CSSProperties
            }
        >
            {children}
        </div>
    );
};

interface ISearchbarContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    disableExtraMargins?: boolean;
}

export const SearchbarContainer = (props: ISearchbarContainerProps) => {
    const {disableExtraMargins, className, ...restProps} = props;

    return (
        <div
            {...restProps}
            className={cx(
                searchbarContainerBaseStyle,
                disableExtraMargins ? searchbarContainerTightMarginsStyle : searchbarContainerDefaultMarginsStyle,
                className
            )}
        />
    );
};

const relativeWrapperBaseStyle = css({
    position: "relative",
    display: "flex",
    flexGrow: 1,
    flexShrink: 1,
    flexDirection: "column",
    padding: 2,

    md: {
        padding: 0,
        maxHeight: "100%",
        width: "100%"
    }
});

const relativeWrapperOutsideModalStyle = css({
    height: "calc(100% - 6rem)"
});

const relativeWrapperNoPaddingStyle = css({
    padding: 0
});

const searchDropdownBaseStyle = css({
    width: "100%",
    flexDirection: "column",
    animationStyle: "fade-in",

    md: {
        boxShadow: "4",
        minWidth: "var(--search-dropdown-min-width, 600px)"
    },

    lg: {
        minWidth: "var(--search-dropdown-districts-min-width, var(--search-dropdown-min-width, 600px))"
    }
});

const searchDropdownVisibleStyle = css({
    display: "flex"
});

const searchDropdownHiddenStyle = css({
    display: "none"
});

const searchDropdownAnchoredStyle = css({
    position: "absolute",
    py: 1,
    px: 1,
    margin: 0,
    zIndex: 1001,
    top: "var(--search-dropdown-top)",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)"
});

const searchDropdownWithDistanceStyle = css({
    left: "120px",
    width: "calc(100% - 120px)",

    "@media (max-width: 480px)": {
        left: 0,
        width: "100%"
    }
});

const searchDropdownWithoutDistanceStyle = css({
    left: 0,
    width: "100%"
});

const searchDropdownPortaledStyle = css({
    position: "fixed",
    margin: 0,
    borderRadius: 2,
    backgroundColor: "var(--colors-white)"
});

const searchDropdownDistrictsMinWidthStyle = css({
    lg: {
        minWidth: "836px"
    }
});

const searchDropdownContentWrapperStyle = css({
    py: 2,
    px: 1
});

const distanceWrapperRightStyle = css({
    maxWidth: "120px",
    width: "30%",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 0,
    height: "50px"
});

const searchbarContainerBaseStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    maxHeight: "4.8rem",

    "& > div:nth-of-type(2)": {
        marginBottom: 0,
        width: "100%",
        zIndex: "initial"
    }
});

const searchbarContainerTightMarginsStyle = css({
    mb: 0.5,

    "@media (max-width: 1024px)": {
        mb: 0.5
    }
});

const searchbarContainerDefaultMarginsStyle = css({
    "@media (max-width: 1024px)": {
        mb: 3
    }
});

const tabsWrapperStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: "var(--colors-gray-200)",
    overflow: "hidden",
    maxHeight: "4.8rem"
});

const tabStyle = css({
    width: "100%",
    border: 0,
    borderRadius: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "4.8rem",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-background-200)"
    }
});

const tabActiveStyle = css({
    backgroundColor: "var(--colors-background-200)"
});

const tabInactiveStyle = css({
    backgroundColor: "var(--colors-background-100)"
});

const regionPillStyle = css({
    py: 0,
    px: 1,
    height: "4.8rem",
    display: "flex",
    alignItems: "center",
    flexDirection: "row"
});

const regionsHolderStyle = css({
    my: 2,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 1,

    "& > div:last-of-type": {
        marginRight: 0
    }
});

const titleHolderStyle = css({
    pr: 1,
    flexGrow: 1,
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const regionsExpandableStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const regionExpandableButtonStyle = css({
    marginTop: "-2rem",
    width: "100%"
});

const regionMarkerIconStyle = css({
    pr: 1
});

const customExpandableShadowStyle = {
    background: "linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%)"
};
